import { BlogAuthorService } from '../../services/blog-author-service';
import { bindable, EventAggregator, inject, observable, watch } from 'aurelia';
import { IRouteViewModel, Params, route } from '@aurelia/router-lite';
import { DEVICES, SortArr } from '../../resources/constants';
import { BlogService } from '../../services/blog-service';
import { BlogTagService } from '../../services/blog-tag-service';
import { WebsiteService } from '../../services/website-service';
import { websiteShortCode } from '../../environment';
import { BlogCategory, BlogsTags, BlogTagChild, ItemsSortBy, SubCategory } from '../../services/models/blogs/blogsTags';
import './blogs.scss';
import { CdsAutocomplete } from '@chicksgroup/web-components';
import { AuthenticationHandler, Helper } from '../../resources/extensions';
import { BlogsParams } from '../../services/models/blogs/blogsParams';
import { SessionService } from '../../services/session-service';
import { User } from '../../services/models/user';

@route({
    path: [''],
    title: 'Blogs',
})
@inject(BlogService, EventAggregator, WebsiteService, BlogTagService, BlogAuthorService, Helper, AuthenticationHandler, SessionService)
export class Blogs implements IRouteViewModel {
    @bindable user: User | null;
    blogs;
    searchQuery: string;
    allTagsOptions: BlogsTags[];
    blogRoute: string;
    category: string | BlogsParams;
    @observable tagSelected: BlogsTags | string | undefined;
    lastBlogs;
    homePageRoute: string;
    routerSubscription;
    params: BlogsParams;
    blogsCount: number;
    count: number = 1;
    @observable page: number = 1;
    perPage: number = 15;
    sizeChanged: () => void;
    width: number = window.innerWidth;
    viewportBucket: 'phone' | 'tablet' | 'desktop' = this.getViewportBucket(window.innerWidth);
    items: ItemsSortBy[];
    authors: ItemsSortBy[];
    @observable categories: ItemsSortBy[];
    subCategories: SubCategory[] = [];
    pageLoading: boolean = true;
    postLoading: boolean = false;
    skeletonPosts = Array.from({ length: 15 });
    blogsResults = true;
    shortCode: string = websiteShortCode() ?? 'CG';
    firstLoad: boolean = true;
    sortRef: CdsAutocomplete;
    authorRef: CdsAutocomplete;
    categoryRef: CdsAutocomplete;
    blogposts: any;
    fetchingBlogs: boolean;
    DEVICES = DEVICES;
    menuRef;
    selectedValue: any;
    selectedLabel: any;
    scrollView: any;
    sort: string | undefined;
    blogChildId: number | undefined;
    hrefLang: string | undefined;
    categoriesSearchQuery: string = '';
    clearingFilterLabels: string[] = [];
    private readonly requiredWebComponents = [
        'cds-autocomplete',
        'cds-autocomplete-item',
        'cds-search',
        'cds-menu',
        'cds-icon-button',
        'cds-icon',
        'cds-no-results',
        'cds-skeleton',
        'cds-pagination',
    ];

    constructor(
        private blogService: BlogService,
        private eventAggregator: EventAggregator,
        private websiteService: WebsiteService,
        private blogTagService: BlogTagService,
        private blogAuthorService: BlogAuthorService,
        private helper: Helper,
        private authHandler: AuthenticationHandler,
        private sessionService: SessionService
    ) {}

    async loading(params: Params | BlogsParams) {
        this.pageLoading = true;
        this.user = this.sessionService?.currentUser;
        this.hrefLang = (await this.sessionService?.getLanguage())?.language?.hrefLang ?? undefined;
        if (window.location.search.includes('autologin=true') && !this.user) {
            this.redirectToSignIn();
        } else if (window.location.search.includes('autologin=true') && this.user) {
            const sanitizedUrl = window.location.href.replace('autologin=true', '').replace(/[\?&]$/, '');
            window.history.replaceState({}, '', sanitizedUrl);
        }

        const isResources = window.location.hostname.includes('resources');
        this.params = params.category || params.page ? params : { category: 'all', page: '1' };
        if (!isResources && this.params.category?.toLowerCase() === 'csr') {
            this.params = { ...this.params, category: 'all' };
        } else if (isResources && this.params.category?.toLowerCase() !== 'csr') {
            this.params = { ...this.params, category: 'csr' };
        }

        let getItem = localStorage.getItem('blogsSelectedCategory');
        if (getItem?.length! > 0) {
            if (getItem?.includes('-')) {
                getItem = getItem?.replaceAll('-', ' ');
            }
            this.params = { ...this.params, category: getItem ?? 'all' };
            localStorage.removeItem('blogsSelectedCategory');
        }

        this.category = this.params.category ? this.params.category : 'all';
    }

    getScrollView() {
        this.scrollView = document.querySelector('#main-page-host .simplebar-content-wrapper');
    }

    private waitForWebComponentsReady() {
        if (typeof window === 'undefined' || !window.customElements) {
            return Promise.resolve();
        }

        return Promise.all(this.requiredWebComponents.map((tagName) => window.customElements.whenDefined(tagName)));
    }


    async attaching() {
        this.getScrollView();
        this.items = SortArr;
        const [pages, allTagsOptions] = await Promise.all([
            this.websiteService.getPagesByWebsiteShortcode(),
            this.blogTagService.getAll(),
            this.waitForWebComponentsReady(),
        ]);
        this.allTagsOptions = allTagsOptions;
        this.page = 1;
        this.blogRoute = pages.find((x) => x.name === 'Blog')?.routeName ?? 'blog';
        this.homePageRoute = pages.find((x) => x.name === 'Home')?.routeName ?? '';
        this.createListAuthors();
        this.createListCategories();
        await this.pageByWidth(true);
        this.createListSubCategories();
        this.setupResizeHandler();
    }

    attached() {
        this.loadData();
        this.handleValue();
    }

    loadData() {
        this.handleEventSubscriptions();
        setTimeout(() => {
            this.categoryRef.setValue('All');
        }, 100);
        this.pageLoading = false;
    }

    redirectToSignIn() {
        void this.authHandler.redirectToSSO({ autologin: true, redirectUrl: window.location.origin });
    }

    detaching() {
        window.removeEventListener('resize', this.sizeChanged);
        this.routerSubscription?.dispose();
    }

    setupResizeHandler() {
        this.sizeChanged = () => {
            const nextWidth = window.innerWidth;
            const nextViewportBucket = this.getViewportBucket(nextWidth);

            this.width = nextWidth;

            if (nextViewportBucket === this.viewportBucket) {
                return;
            }

            this.viewportBucket = nextViewportBucket;
            void this.pageByWidth();
        };
        window.addEventListener('resize', this.sizeChanged);
    }

    private getViewportBucket(width: number): 'phone' | 'tablet' | 'desktop' {
        if (width <= DEVICES.PHONE) return 'phone';
        if (width <= DEVICES.DESKTOP) return 'tablet';
        return 'desktop';
    }

    async searchBlog(event: InputEvent) {
        this.searchQuery = (event?.target as HTMLInputElement)?.value ?? '';
        this.helper.debounce(this, 'fetchingBlogs', 'loadingBlogsTimeout', 700, async () => {
            if (this.width >= this.DEVICES.DESKTOP && !this.lastBlogs) {
                this.lastBlogs = await this.blogService.getPublishedBlogPosts(5, 1);
            } else if (this.width < this.DEVICES.DESKTOP && !this.lastBlogs) {
                this.lastBlogs = await this.blogService.getPublishedBlogPosts(4, 1);
            }

            if (!this.searchQuery) {
                await this.pageChanged();
                return (this.postLoading = false);
            }

            await this.pageChanged();

            if (this.blogsCount === 0) {
                this.blogposts = [];
                this.count = this.page = 1;
                this.blogsResults = false;
                return (this.postLoading = false);
            }

            this.blogsResults = true;
            return (this.postLoading = false);
        });
    }

    handleEventSubscriptions() {
        this.routerSubscription = this.eventAggregator.subscribe('router:navigation:processing', (payload: any) => {
            const regExp = new RegExp(`(\\W|^)${this.blogRoute}(\\W|$)`, 'i');
            if (
                payload.instruction.params?.blog ||
                payload.instruction.params?.alias ||
                !payload.instruction.fragment.match(regExp)
            ) {
                return;
            }

            const isResources = window.location.hostname.includes('resources');
            let category = payload.instruction.params?.category;

            if (!isResources && category?.toLowerCase() === 'csr') {
                category = 'all';
            } else if (isResources && category?.toLowerCase() !== 'csr') {
                category = 'csr';
            }

            this.category = category ?? 'all';
            this.page = parseInt(payload.instruction.params?.page) ? parseInt(payload.instruction.params?.page) : 1;
        });
    }

    async pageByWidth(syncCategoryWithRoute: boolean = false) {
        this.postLoading = true;
        if (this.width <= DEVICES.PHONE) {
            this.perPage = 6;
            this.skeletonPosts = Array.from({ length: 6 });
        } else if (this.width <= DEVICES.DESKTOP) {
            this.perPage = 8;
            this.skeletonPosts = Array.from({ length: 8 });
        } else {
            this.perPage = 15;
            this.skeletonPosts = Array.from({ length: 15 });
        }

        if (syncCategoryWithRoute && this.category !== 'all') {
            await this.tagClickHandler(this.category as BlogsTags);
            return this.postLoading = false;
        }

        await this.pageChanged();
        return this.postLoading = false;
    }

    changeSelectHandler(event: CustomEvent) {
        const autocomplete = event.target as { close?: () => void; label?: string };
        const selectedValue = event?.detail?.selected?.value;
        const subCategory = this.subCategories.find(sc => sc.label === autocomplete.label);
        subCategory?.items?.forEach(item => (item.selected = item.value === selectedValue));
        autocomplete?.close?.();

        return this.pageChanged(event);
    }

    checkIfAllCategoriesSelected() {
        const allSelected = this.categories.every((category) => category.selected);
        if (allSelected) return this.categoryRef?.setValue(this.categories[0].label, false);
    }

    firstLoadHandler() {
        if (this.firstLoad) {
            this.firstLoad = false;
        } else {
            this.postLoading = true;
            this.scrollToBlogs();
        }
    }

    childTagsFromSubCategories(subcategory: string, subcategoryLabel: string): BlogTagChild | undefined {
        let childTagSelected: BlogTagChild | undefined = undefined;
        const filterTags = this.allTagsOptions.filter(tag => tag.blogTagChilds.length > 0);

        filterTags?.map((tag) => {
            tag.blogTagChilds.filter((childTag) => {
                if (childTag.name === subcategory && tag.name === subcategoryLabel) {
                    childTagSelected = childTag;
                }
            });
        });

        return childTagSelected;
    }

    handleSort(event) {
        this.sort = event?.detail?.selected?.value;
        this.pageChanged();
    }

    async pageChanged(event?) {
        event && (this.selectedValue = event?.detail?.selected?.value);
        event && (this.selectedLabel = event?.target?.label || '');
        const tagsValues = this.categories?.filter((x: ItemsSortBy) => x.selected);
        const tagsArr = tagsValues?.filter((x: ItemsSortBy) => x.value !== null).map((x: ItemsSortBy) => x.value || undefined);
        const authorAliasValue = this.authors?.find((x: ItemsSortBy) => x.selected)?.value;
        const noTagsSelected = tagsArr?.length === 0 && !this.categories?.find((c) => c.label === 'All')?.selected;
        const selectedSubcategoryTags: string[] = [];
        const selectedChildTagId = this.childTagsFromSubCategories(this.selectedValue, this.selectedLabel)?.id;
        const finalTagsArr = tagsArr;
        const isCsr = this.params.category === 'csr';
        this.firstLoadHandler();

        this.blogs = await this.blogService.filterBlogs({
            tag: isCsr ? 'csr' : undefined,
            tags: isCsr ? undefined : (noTagsSelected ? (!this.tagSelected ? [''] : undefined) : selectedSubcategoryTags.length > 0 ? undefined : finalTagsArr),
            blogTagChilds: selectedChildTagId || this.blogChildId,
            content: this.searchQuery,
            page: this.page,
            pageSize: this.perPage,
            order: this.sort ?? 'popular',
            tagHidden: isCsr ? true : (typeof this.tagSelected === 'object' && this.tagSelected !== null ? this.tagSelected.hideFromAll : false),
            authorAlias: authorAliasValue === 'all' ? undefined : authorAliasValue,
            websites: [this.shortCode],
            hrefLang: this.hrefLang
        });

        this.checkIfAllCategoriesSelected();
        this.blogposts = this.blogs?.blogPosts;
        this.blogsCount = this.blogs?.totalCount ?? 0;
        this.count = Math.ceil(this.blogsCount / this.perPage);
        if (this.page > this.count && this.count > 0) {
            this.page = this.count;
        }
        this.scrollToTop();

        return (this.postLoading = false);
    }

    @watch('category')
    handleCategoryChanged() {
        const newTag = this.findTagOption(this.category as string);
        this.tagClickHandler(newTag as BlogsTags);
    }

    tagClickHandler(tag: BlogsTags | string) {
        const allTags = this.allTagsOptions.filter((tags) => tags.blogTagChilds.length > 0);
        const childTags = allTags.flatMap(tag => tag.blogTagChilds);
        const childTagSelected = childTags.find(child => child.name === tag?.name);
        if (childTagSelected) {
            const parentCategory = allTags.find(tag => tag.blogTagChilds.some(child => child.blogTagId === childTagSelected.blogTagId));
            this.blogChildId = childTagSelected.id;
            this.createListCategories(parentCategory?.name);
            this.createListSubCategories(childTagSelected.name);
            return this.pageChanged();
        }
        if (typeof tag === 'object' && tag.slug) {
            this.createListCategories(tag.slug as string);
        } else {
            this.createListCategories(tag as string);
        }
        return this.pageChanged();
    }

    findTagOption = (value: string) => this.allTagsOptions.find((tag: BlogsTags) => tag.slug === value);

    scrollToTop() {
        this.scrollView.scrollTo({ top: 0, behavior: 'smooth' });
    }

    scrollToBlogs() {
        this.scrollView.scrollTo({ top: 0, behavior: 'smooth' });
    }

    get titleByWebsite(): string {
        return this.shortCode !== 'CX' ? 'Video Games Blog' : 'Crypto Blog';
    }

    get subCategoriesExist(): boolean {
        return this.subCategories.length > 0;
    }

    handlePagination(value: CustomEvent): void | number {
        return (this.page = parseInt(value.detail.page));
    }

    async createListAuthors() {
        this.authors = [];
        const authorsFilter = await this.blogAuthorService.getByWebsiteCode();
        const newAuthors = Array.isArray(authorsFilter)
            ? authorsFilter.map((author) => {
                return {
                    label: `${author.firstName} ${author.lastName}`,
                    value: author.alias,
                    selected: false,
                };
            })
            : [];
        const allItem = { label: 'All', value: 'all', selected: true };
        this.authors.push(...newAuthors);
        this.authors.unshift(allItem);
        return this.authors;
    }

    createListCategories(tagCategory: string = '') {
        this.categories = [];
        const categoriesFilter = this.allTagsOptions?.filter(tag => tag.websites.find(web => web.website.shortCode === this.shortCode && !tag.hideFromAll));
        let newCategories = Array.isArray(categoriesFilter)
            ? categoriesFilter.map((category) => {
                return {
                    label: category.name,
                    value: category.name,
                    selected: this.params.category === 'all',
                };
            })
            : [];

        if (!newCategories.some((category: ItemsSortBy) => category.label === 'All')) {
            newCategories.unshift({
                label: 'All',
                value: 'all',
                selected: this.params.category === 'all',
            });
        }
        if (tagCategory) {
            newCategories = newCategories.map(cat => ({ ...cat, selected: cat.value.toLowerCase().replace('&', 'and') === tagCategory.toLowerCase() }));
        }
        this.categories.push(...newCategories);
        return this.categories;
    }

    createListSubCategories(childTagCategory: string = '') {
        this.subCategories = [];
        const selectedCategories = this.categories.filter((category) => category.selected);

        for (const category of selectedCategories) {
            const tagOption = this.allTagsOptions.find((tag) => tag.name === category.label);

            if (tagOption && tagOption.blogTagChilds && tagOption.blogTagChilds.length > 0) {
                const items: ItemsSortBy[] = tagOption.blogTagChilds.map((childTag) => ({
                    label: childTag.name,
                    value: childTag.name,
                    selected: false,
                }));

                items.unshift({
                    label: 'All',
                    value: 'all',
                    selected: true,
                });

                this.subCategories.push({
                    label: tagOption.name as string,
                    value: tagOption.slug,
                    items,
                });
            }

            if (childTagCategory) {
                const selectChild = this.subCategories[0].items?.map(item => item.label === childTagCategory ? { ...item, selected: true } : { ...item, selected: false });
                this.subCategories[0].items = selectChild;
            }
        }
        return this.subCategories;
    }

    async clearFilters() {
        this.clearingFilterLabels = this.subCategories
            .filter(sc => sc.items?.some((item, i) => i > 0 && item.selected))
            .map(sc => sc.label);
        const resetAuthor = this.authors?.some((author, i) => i > 0 && author.selected);
        const resetCategories = this.categories?.some(category => !category.selected);
        if (this.clearingFilterLabels.length) {
            this.eventAggregator.publish('clear-filters', null);
            this.subCategories
                .filter(sc => this.clearingFilterLabels.includes(sc.label))
                .forEach(sc => sc.items?.forEach((item, i) => (item.selected = i === 0)));
        }
        this.searchQuery = '';
        this.selectedValue = null;
        this.selectedLabel = null;
        this.sort = 'popular';
        if (resetAuthor) {
            this.authors?.forEach(author => (author.selected = false));
            this.authorRef.selectItem(0);
        }
        if (resetCategories) {
            this.categories?.forEach(category => (category.selected = true));
            this.checkIfAllCategoriesSelected();
            this.createListSubCategories();
        }
        this.category = 'all';
        this.page = 1;

        await this.pageChanged();
        this.clearingFilterLabels = [];
    }

    handleMenuOpened() {
        this._setMenuOverflow('visible');
    }

    handleMenuOpening() {
        this._setMenuOverflow('hidden');
    }

    handleMenuClosed() {
        this._setMenuOverflow('hidden');
    }

    _setMenuOverflow(value) {
        if (this.menuRef instanceof HTMLElement) {
            const menuContainer = this.menuRef.shadowRoot ? this.menuRef.shadowRoot.querySelector('.menu-container') : null;
            menuContainer && ((menuContainer as HTMLElement).style.display = 'block');
            this.menuRef.style.setProperty('--cds-menu-overflow', value);
        }
    }

    get lowRangePagination() {
        return (this.page - 1) * this.perPage + 1;
    }

    get highRangePagination() {
        return Math.min(this.page * this.perPage, this.blogsCount);
    }

    checkboxStatesHandler(object: BlogCategory) {
        const allCategory = this.categories.find((category) => category.label === 'All');
        const filterCategories = this.categories.filter((category) => category.label !== 'All');
        this.handleAllSelection(object);

        if (object.label !== 'All') {
            allCategory!.selected ? (allCategory!.selected = false) : null;
            object.selected = !object.selected;
        }

        if (filterCategories.every((category) => category.selected)) allCategory!.selected = true;

        this.handleValue();
        this.createListSubCategories();
        this.pageChanged();
    }

    handleValue() {
        const selectedCategoriesLabels = this.categories.map(category => category.label !== 'All' && category.selected ? category.label : null).filter(label => label !== null).join(', ') || 'All';
        const categoriesUnselected = this.categories.every(category => !category.selected);
        categoriesUnselected ? this.categoryRef.setValue('None', false) : this.categoryRef.setValue(selectedCategoriesLabels, false);
    };

    handleAllSelection(object: BlogCategory) {
        if (object.label === 'All' && object.selected) {
            this.categories.map((category) => {
                category.selected = false;
            });
        } else if (object.label === 'All' && !object.selected) {
            this.categories.map((category) => {
                category.selected = true;
            });
        }
    }

    checkboxSearchTrigger(event: CustomEvent) {
        this.categoriesSearchQuery = (event?.detail?.query as string) ?? '';
        this.categories = this.categories.map(category => {
            const querySearch = category.label.toLowerCase().includes(this.categoriesSearchQuery.toLowerCase());
            if (this.categoriesSearchQuery === 'All') return { ...category, hidden: false };
            return { ...category, hidden: !querySearch };
        });
    }

    onClosingRestartCategories() {
        this.categories = this.categories.map(category => ({ ...category, hidden: false }));
    }

    onOpenedRestartQuery() {
        this.categoryRef.setValue('', false);
    }

    get allCategoriesHiddenCheck(): boolean {
        return this.categories?.every(category => category?.hidden);
    }
}
